import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  savePosProduct, setPosStock, setPosReserve,
  posPackSize, posReserveUnits, posUnitsFromEntry, describePosUnits,
  posUnitNoun, posPackNoun, posReserveEnabled,
} from '../utils/posUtil';
import { POS_PACK_SIZE_KEY, POS_COST_PRICE_KEY } from '../constants/posConst';
import PosAddStockModal from './PosAddStockModal';
import PosAddReserveModal from './PosAddReserveModal';
import PosDeleteProductModal from './PosDeleteProductModal';
import PosCustomizationField from './PosCustomizationField';
import PosPricingSection from './PosPricingSection';
import { posProductCustomizations, posProductExtras } from '../utils/posCustomizationUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import '../css/Pos.css';

/**
 * Register or edit one product.
 *
 * @param {object} props
 * STOCK IS NOT ENTERED HERE WHEN REGISTERING. A new product's shelf starts
 * empty and is filled by replenishing from the storeroom, so the only opening
 * figure this form asks for is the RESERVE — and only on the Reserve tab,
 * which is where a product is registered.
 *
 * @param {object|null} props.product - null to register a new one
 * @param {string[]} props.categories
 * @param {() => void} props.onClose
 * @param {string} [props.scope='reserve'] - which tab this was opened from.
 *   On 'active' the reserve block is hidden outright: the storeroom is managed
 *   on its own tab, and two places to change it is how the two come to
 *   disagree about which one is authoritative.
 * @returns {JSX.Element}
 */
function PosProductModal({ product, categories, onClose, scope = 'reserve' }) {
  const showReserve = scope !== 'active';
  const { config, clientId } = useConfig();
  // With a reserve, stock added to the shelf is taken OUT of it; without one,
  // the shelf is the only stock and a new product is given an opening count.
  const reserveOn = posReserveEnabled(config);
  const [openingStock, setOpeningStock] = useState('');
  const { staffEmail } = usePosSession();
  const [name, setName] = useState(product?.name || '');
  const [description, setDescription] = useState(product?.description || '');
  const [category, setCategory] = useState(product?.category || '');
  const [price, setPrice] = useState(product?.price ?? '');
  // The "puhunan" for one. Blank means not entered, which is kept apart from 0.
  const [costPrice, setCostPrice] = useState(product?.[POS_COST_PRICE_KEY] ?? '');
  // IS THIS PRODUCT SOLD IN PACKS? The brief's checkbox. Unticked, the whole
  // pack half of the form disappears rather than sitting there asking how many
  // pieces are in a box that does not exist.
  const [packed, setPacked] = useState(posPackSize(product) > 0);
  const [packSize, setPackSize] = useState(posPackSize(product) ? String(posPackSize(product)) : '');
  const [unitLabel, setUnitLabel] = useState(product?.unitLabel || '');
  const [packLabel, setPackLabel] = useState(product?.packLabel || '');
  // Opening reserve, only asked for when REGISTERING — an edit never writes a
  // count, for the same reason it never writes the shelf.
  const [reservePacks, setReservePacks] = useState('');
  const [reserveLoose, setReserveLoose] = useState('');
  // The stored reserve, and the field editing it, kept apart exactly as the
  // shelf's two are: the difference between them is the test for "did anyone
  // actually change this?".
  const [reserveNow, setReserveNow] = useState(posReserveUnits(product));
  const [reserveField, setReserveField] = useState(String(posReserveUnits(product)));
  // Customization options (sugar level, add-ons…). Selling questions, so they
  // live beside the price on the Active tab, like the price does.
  const [customizations, setCustomizations] = useState(() => posProductCustomizations(product));
  // Paid add-ons ("extra pearl"), set up in the same block as the options.
  const [extras, setExtras] = useState(() => posProductExtras(product));
  const [customizable, setCustomizable] = useState(
    () => posProductCustomizations(product).length > 0 || posProductExtras(product).length > 0,
  );
  const [imageFile, setImageFile] = useState(null);
  // Shows the newly chosen file before it is saved; falls back to the stored
  // image so editing a product doesn't look like it lost its picture.
  const [preview, setPreview] = useState(product?.imageUrl || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [addingStock, setAddingStock] = useState(false);
  const [addingReserve, setAddingReserve] = useState(false);
  // What the stock is now, after any delivery recorded from this form. Held
  // separately from `supplies` because the two are no longer the same thing:
  // `supplies` is only ever SENT when registering a product, while this is
  // only ever SHOWN.
  const [stockNow, setStockNow] = useState(Math.max(0, Math.floor(Number(product?.supplies) || 0)));
  // What the count field currently holds, as typed. Separate from stockNow,
  // which is what the database last told us — the difference between the two
  // is the whole test for "did someone actually change this?".
  const [stockField, setStockField] = useState(String(Math.max(0, Math.floor(Number(product?.supplies) || 0))));

  const handlePickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImageFile(file);
    // Object URL rather than a FileReader: nothing needs the bytes here, only
    // something to point an <img> at until save compresses it properly.
    setPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await savePosProduct(clientId, product, {
        name, description, category, price, imageFile,
        // Unticking "sold in packs" stores 0, which is what turns every pack
        // control off — including the one in the replenish dialog.
        [POS_PACK_SIZE_KEY]: packed ? packSize : 0,
        unitLabel,
        packLabel,
        reservePacks,
        reserveUnits: reserveLoose,
        ...(!reserveOn && !product ? { openingSupplies: openingStock } : {}),
        // Only sent from the form that SHOWS the options (the Active tab), so
        // a save from the Reserve tab leaves them exactly as they were.
        // Unticked saves an empty list, which removes them.
        ...(!showReserve ? {
          // Sent only from the form that SHOWS it, like the price.
          [POS_COST_PRICE_KEY]: costPrice,
          customizations: customizable ? customizations : [],
          extras: customizable ? extras : [],
        } : {}),
      }, staffEmail);

      // THE COUNT IS WRITTEN ONLY IF SOMEONE CHANGED IT, and through its own
      // call, because it is a different kind of change: savePosProduct merges
      // the product's details and never touches stock.
      //
      // The comparison is what keeps an unrelated edit safe. Writing the field
      // unconditionally is how a form that opened at 24 and was saved two
      // minutes later — after two sales — put 24 back and erased them, which
      // is exactly what this guard exists to prevent.
      if (product && String(stockField).trim() !== String(stockNow)) {
        const corrected = await setPosStock(clientId, product, stockField, staffEmail, { fromReserve: reserveOn });
        setStockNow(corrected);
        setStockField(String(corrected));
      }

      // The storeroom count follows the same rule and for the same reason: a
      // form opened before a replenishment and saved after it must not put the
      // old figure back.
      if (product && String(reserveField).trim() !== String(reserveNow)) {
        const corrected = await setPosReserve(clientId, product, reserveField, staffEmail);
        setReserveNow(corrected);
        setReserveField(String(corrected));
      }

      onClose();
    } catch (err) {
      setError(describeWriteError(err, { what: 'this product', path: `posTenants/${clientId}/products` }));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    // No backdrop click-to-close: this form holds unsaved work
    // (CLAUDE.md > Behavior). The × and Cancel are the ways out.
    <div className="pos-modal-overlay">
      <div className="pos-modal" role="dialog" aria-modal="true" aria-label={product ? 'Edit product' : 'Add new product'}>
        <header className="pos-modal-head">
          <h3>{product ? 'Edit product' : 'Add new product'}</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          <div className="pos-image-row">
            {/* `contain`, not `cover` — a product shot is usually a tall
                bottle or a boxed item on white, and cropping it to a square
                cuts the top off the thing being sold. */}
            <div className={`pos-image-preview${preview ? '' : ' empty'}`}>
              {preview ? <img src={preview} alt="" /> : <span>No image</span>}
            </div>
            <div className="pos-image-actions">
              <label className="pos-btn pos-btn-ghost pos-image-pick">
                {preview ? 'Replace image' : 'Choose image'}
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handlePickImage}
                  style={{ display: 'none' }}
                />
              </label>
              <p className="pos-image-hint">
                JPG, PNG or WebP. Resized and compressed when you save.
              </p>
            </div>
          </div>

          <label className="pos-field">
            <span>Product name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="pos-field">
            <span>Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Uncategorised</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
              {/* A product edited after its category was removed keeps that
                  category, so it stays selectable here rather than silently
                  resetting to Uncategorised on the next save. */}
              {category && !categories.includes(category) && (
                <option value={category}>{category} (removed)</option>
              )}
            </select>
          </label>

          <label className="pos-field">
            <span>Description</span>
            {/* Two rows, not three. Most descriptions are a size or a flavour
                ("500ML"), and the third row was empty space that pushed the
                price and stock row below the fold on a counter tablet. It is
                still resizable for the rare long one. */}
            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>

          <div className="pos-field-row">
            {/* TWO WAYS IN, because they are two different facts. Typing here
                sets the count to exactly what you type — a stocktake, which
                overrules whatever the till believed. Add supplies adds a
                delivery to whatever is on the shelf at the moment it commits,
                so it cannot lose a sale rung up while this form was open.
                The typed count is only written if it was actually CHANGED;
                leaving it alone writes nothing, which is what keeps an edit to
                a name or a price from reinstating a stale number. */}
            {product && (
              <div className="pos-field">
                <span>Available supplies</span>
                <div className="pos-stock-row">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="pos-stock-input"
                    value={stockField}
                    onChange={(e) => setStockField(e.target.value)}
                    disabled={saving}
                    aria-label="Available supplies"
                  />
                  <button
                    type="button"
                    className="pos-stock-add"
                    onClick={() => setAddingStock(true)}
                    disabled={saving}
                  >
                    Add supplies
                  </button>
                </div>
                {String(stockField).trim() !== String(stockNow) && (
                  <span className="pos-stock-hint">
                    Saving sets the count to exactly this, replacing {stockNow}.
                    {reserveOn && Number(stockField) > stockNow
                      ? ` The extra ${Number(stockField) - stockNow} will be taken from the reserve.`
                      : ''}
                  </span>
                )}
                {reserveOn && (
                  <span className="pos-theme-hint">
                    In reserve: {describePosUnits(reserveNow, product)}. Adding to the shelf takes from the reserve.
                  </span>
                )}
              </div>
            )}
            {/* NO RESERVE, NEW PRODUCT: the shelf is where it starts, so this
                is where its opening count is asked for. */}
            {!product && !reserveOn && (
              <label className="pos-field">
                <span>Opening stock</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={openingStock}
                  onChange={(e) => setOpeningStock(e.target.value)}
                  disabled={saving}
                  placeholder="0"
                />
                <span className="pos-theme-hint">How many you have to sell right now.</span>
              </label>
            )}
          </div>

          {/* PRICING LIVES ON THE ACTIVE TAB, in its own section. It is what
              the product sells for (and costs), and the shelf is where selling
              happens — the storeroom only records what was bought and how it
              is packed. Hidden rather than disabled on the Reserve tab, and
              savePosProduct leaves both prices alone when the form did not
              carry them. */}
          {!showReserve && (
            <PosPricingSection
              price={price}
              onPriceChange={setPrice}
              costPrice={costPrice}
              onCostPriceChange={setCostPrice}
              disabled={saving}
            />
          )}

          {!showReserve && (
            <PosCustomizationField
              enabled={customizable}
              onEnabledChange={setCustomizable}
              options={customizations}
              onChange={setCustomizations}
              extras={extras}
              onExtrasChange={setExtras}
              disabled={saving}
            />
          )}

          {/* ---------- Reserve stock ---------- */}
          {/* Its own block, below the shelf, because it answers a different
              question: not "what can I sell right now" but "what is still in
              the storeroom behind it". */}
          {showReserve && (
          <div className="pos-reserve-block">
            <label className="pos-check">
              <input
                type="checkbox"
                checked={packed}
                onChange={(e) => setPacked(e.target.checked)}
                disabled={saving}
              />
              <span>
                <strong>This product comes in packs</strong>
                <em>A box, case or carton holding a fixed number of pieces</em>
              </span>
            </label>

            {packed && (
              <div className="pos-field-row">
                <label className="pos-field">
                  <span>Pieces in one pack</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={packSize}
                    onChange={(e) => setPackSize(e.target.value)}
                    disabled={saving}
                    placeholder="24"
                  />
                </label>
                <label className="pos-field">
                  <span>A pack is called</span>
                  <input
                    type="text"
                    value={packLabel}
                    onChange={(e) => setPackLabel(e.target.value)}
                    disabled={saving}
                    placeholder="box"
                    maxLength={24}
                  />
                </label>
                {/* The noun, so a storeroom reads "3 boxes and 10 bottles"
                    rather than "3 boxes and 10 pieces" — and so a club selling
                    shirts is not told it has bottles. */}
                <label className="pos-field">
                  <span>A piece is called</span>
                  <input
                    type="text"
                    value={unitLabel}
                    onChange={(e) => setUnitLabel(e.target.value)}
                    disabled={saving}
                    placeholder="piece"
                    maxLength={24}
                  />
                </label>
              </div>
            )}

            {product ? (
              <div className="pos-field">
                <span>In reserve</span>
                {/* TWO WAYS IN, exactly as the shelf has: typing sets the
                    count to what you type (a storeroom stocktake), while Add
                    delivery adds what arrived to whatever is there at the
                    moment it commits — so it cannot lose a replenishment made
                    while this form sat open. */}
                <div className="pos-stock-row">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="pos-stock-input"
                    value={reserveField}
                    onChange={(e) => setReserveField(e.target.value)}
                    disabled={saving}
                    aria-label="Reserve stock, in pieces"
                  />
                  <button
                    type="button"
                    className="pos-stock-add"
                    onClick={() => setAddingReserve(true)}
                    disabled={saving}
                  >
                    Add supplies
                  </button>
                </div>
                <span className="pos-reserve-reads">
                  {describePosUnits(reserveField, { ...product, [POS_PACK_SIZE_KEY]: packed ? packSize : 0, unitLabel, packLabel })}
                </span>
                {String(reserveField).trim() !== String(reserveNow) && (
                  <span className="pos-stock-hint">
                    Saving sets the reserve to exactly this, replacing {reserveNow}.
                  </span>
                )}
                <span className="pos-stock-hint">
                  Counted in pieces, however it is packed. Use Replenish on the Reserve
                  Stocks tab to move it to the shelf.
                </span>
              </div>
            ) : (
              <div className="pos-field-row">
                {packed && (
                  <label className="pos-field">
                    <span>Opening reserve &mdash; whole {posPackNoun({ packLabel }, 2)}</span>
                    <span className="pos-theme-hint pos-theme-input-hint">
                      Unopened {posPackNoun({ packLabel }, 2)} in the storeroom
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={reservePacks}
                      onChange={(e) => setReservePacks(e.target.value)}
                      disabled={saving}
                      placeholder="0"
                    />
                  </label>
                )}
                <label className="pos-field">
                  <span>Opening reserve &mdash; loose {posUnitNoun({ unitLabel }, 2)}</span>
                  {/* THE TWO FIELDS ADD UP, and that has to be said: a form
                      showing "packs" above "pieces" reads as a choice between
                      them, so someone with ten boxes and ten loose fills in
                      one box and loses the other. */}
                  <span className="pos-theme-hint pos-theme-input-hint">
                    {packed
                      ? `Singles not in a ${posPackNoun({ packLabel }, 1)} — fill in both if you have both`
                      : 'How many are in the storeroom, not yet on the shelf'}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={reserveLoose}
                    onChange={(e) => setReserveLoose(e.target.value)}
                    disabled={saving}
                    placeholder="0"
                  />
                  {!packed && <span className="pos-theme-hint">Leave blank if none yet.</span>}
                </label>
              </div>
            )}

            {/* What the two opening fields add up to, in words — the point at
                which "3 packs and 10 pieces" becomes a number someone can
                check against the storeroom. */}
            {!product && posUnitsFromEntry({ packs: reservePacks, units: reserveLoose }, packed ? packSize : 0) > 0 && (
              <p className="pos-reserve-preview">
                Opening reserve:{' '}
                <strong>
                  {describePosUnits(
                    posUnitsFromEntry({ packs: reservePacks, units: reserveLoose }, packed ? packSize : 0),
                    { [POS_PACK_SIZE_KEY]: packed ? packSize : 0, unitLabel, packLabel },
                  )}
                </strong>
              </p>
            )}

            {/* Said once, where someone registering a product would otherwise
                wonder where the shelf figure went. */}
            {!product && (
              <p className="pos-theme-hint">
                The shelf starts empty, and the price is set on the Active Stocks tab —
                both belong to selling rather than to storing. Use Replenish to carry
                stock out of the storeroom when you are ready to sell it.
              </p>
            )}
          </div>
          )}

          {error && <p className="pos-error">{error}</p>}
          {saving && <div className="pos-modal-saving">{spinnerRing('Saving…')}</div>}
        </div>

        <footer className="pos-modal-foot">
          {/* Opens the confirm as its own modal rather than unfolding a row
              of buttons into this footer, beside Cancel and Save changes. */}
          {product && (
            <button type="button" className="pos-btn pos-btn-danger" onClick={() => setConfirmingDelete(true)} disabled={saving}>
              Delete
            </button>
          )}
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (product ? 'Save changes' : 'Register product')}
          </button>
        </footer>
      </div>

      {confirmingDelete && (
        <PosDeleteProductModal
          product={product}
          // The product is gone, so the form editing it has nothing left to
          // edit — both close together.
          onDeleted={onClose}
          onClose={() => setConfirmingDelete(false)}
        />
      )}

      {addingStock && (
        <PosAddStockModal
          // Given the CURRENT figure rather than the one the form loaded with,
          // so reopening it after one delivery counts up from the new total.
          product={{ ...product, supplies: stockNow, reserveUnits: reserveNow }}
          fromReserve={reserveOn}
          onAdded={(total) => {
            setStockNow(total);
            setStockField(String(total));
            // The storeroom went down by what went onto the shelf.
            if (reserveOn) {
              const moved = total - stockNow;
              setReserveNow((value) => Math.max(0, value - moved));
              setReserveField((value) => String(Math.max(0, Number(value) - moved)));
            }
          }}
          onClose={() => setAddingStock(false)}
        />
      )}

      {addingReserve && (
        <PosAddReserveModal
          // The CURRENT reserve and the pack size as the form has it, so a
          // delivery counts up from the new total and is measured in the pack
          // the person is about to change it to — not the one stored before
          // they opened this.
          product={{
            ...product,
            reserveUnits: reserveNow,
            [POS_PACK_SIZE_KEY]: packed ? Math.max(0, Math.floor(Number(packSize) || 0)) : 0,
            unitLabel: unitLabel || product?.unitLabel,
            packLabel: packLabel || product?.packLabel,
          }}
          onAdded={(total) => { setReserveNow(total); setReserveField(String(total)); }}
          onClose={() => setAddingReserve(false)}
        />
      )}
    </div>,
    document.body,
  );
}

export default PosProductModal;
